import axios from "axios";
import { PRODUCT_RESOURCE_SUBTYPE } from "../constants/resourceLibrary";
import {
  ProductRecord,
  ProductSearchResponse,
  SearchResponse,
} from "../types/globalSearch";

const PAGE_SIZE = 100;

const PRODUCT_PAGE_SIZE = 1000;

const PRODUCT_RUNAWAY_PAGE_LIMIT = 10;

export const globalSearchApi = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  baseURL: `${import.meta.env.VITE_GLOBAL_SEARCH_SERVICE_HOSTNAME}`,
  withCredentials: true,
});

export const GlobalSearchAPI = {
  async quickSearchResourceItem(companyId: number, resourceSubType: string) {
    // Fetch first page to get total count
    const firstResponse = await globalSearchApi.get(
      `/v2/quicksearch?companyId=${companyId}&term=status:effective&resources=registry-item&resourceSubType=${resourceSubType}&size=${PAGE_SIZE}&page=1`,
    );

    const firstPage = SearchResponse.parse(firstResponse.data);

    if (firstPage.total <= PAGE_SIZE) {
      return firstPage;
    }

    // Fetch remaining pages in parallel
    const totalPages = Math.ceil(firstPage.total / PAGE_SIZE);
    const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

    const responses = await Promise.all(
      pageNumbers.map((page) =>
        globalSearchApi.get(
          `/v2/quicksearch?companyId=${companyId}&term=status:effective&resources=registry-item&resourceSubType=${resourceSubType}&size=${PAGE_SIZE}&page=${page}`,
        ),
      ),
    );

    const allResults = [
      ...firstPage.results,
      ...responses.flatMap((res) => SearchResponse.parse(res.data).results),
    ];

    return {
      results: allResults,
      page: 1,
      size: allResults.length,
      total: allResults.length,
    };
  },

  async quickSearchProducts(companyId: number) {
    const results: Array<ProductRecord> = [];
    let page = 1;
    let pageResultCount = 0;

    // The response carries no "more pages" signal: `total` is clamped to `size`
    // and `totalRelation` reads "gte" on every page, including an empty one past
    // the end. A page that comes back short is the only way to know we are done,
    // so keep asking while pages are full.
    do {
      if (page > PRODUCT_RUNAWAY_PAGE_LIMIT) {
        throw new Error(
          `Product search for company ${companyId} did not terminate within ${PRODUCT_RUNAWAY_PAGE_LIMIT} pages`,
        );
      }

      const response = await globalSearchApi.get(
        `/v2/quicksearch?companyId=${companyId}&term=*&resources=registry-item&resourceSubType=${PRODUCT_RESOURCE_SUBTYPE}&size=${PRODUCT_PAGE_SIZE}&page=${page}`,
      );

      const { results: pageResults } = ProductSearchResponse.parse(
        response.data,
      );

      results.push(...pageResults);
      pageResultCount = pageResults.length;
      page++;
    } while (pageResultCount >= PRODUCT_PAGE_SIZE);

    return { results, page: 1, size: results.length, total: results.length };
  },
};
